import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
} from 'react'
import { fetchServerStats, fetchTopPlayers } from '../api/api'

interface TopPlayer {
	Name: string
	CLevel: number
	MasterLevel: number
	Class: string
	ResetCount: number
	MResets: number
	AccountID: string
}

interface ServerStats {
	onlinePlayers: number
	status: string
	totalAccounts: number
	totalCharacters: number
	totalGuilds: number
	topResetCount: number
	topCLevel: number
}

interface ServerContextType {
	topPlayers: TopPlayer[]
	serverStats: ServerStats | null
	sortBy: 'Name' | 'CLevel' | 'ResetCount'
	sortOrder: 'ASC' | 'DESC'
	loading: boolean
	setSortBy: React.Dispatch<
		React.SetStateAction<'Name' | 'CLevel' | 'ResetCount'>
	>
	setSortOrder: React.Dispatch<React.SetStateAction<'ASC' | 'DESC'>>
	handleSort: (field: 'Name' | 'CLevel' | 'ResetCount') => void
}

const ServerContext = createContext<ServerContextType | undefined>(undefined)

export const useServer = () => {
	const context = useContext(ServerContext)
	if (!context) {
		throw new Error('useServer must be used within a ServerProvider')
	}
	return context
}

interface ServerProviderProps {
	children: ReactNode
}

export const ServerProvider: React.FC<ServerProviderProps> = ({ children }) => {
	const [topPlayers, setTopPlayers] = useState<TopPlayer[]>([])
	const [serverStats, setServerStats] = useState<ServerStats | null>(null)
	const [sortBy, setSortBy] = useState<'Name' | 'CLevel' | 'ResetCount'>(
		'CLevel'
	)
	const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')
	const [loading, setLoading] = useState<boolean>(true)

	useEffect(() => {
		const fetchPublicData = async () => {
			try {
				const [statsResponse, topPlayersResponse] = await Promise.all([
					fetchServerStats(),
					fetchTopPlayers(sortBy, sortOrder),
				])
				setServerStats(statsResponse.data)
				setTopPlayers(topPlayersResponse.data)
			} catch (error) {
				console.error('Error fetching public data:', error)
			}
		}

		const fetchData = async () => {
			setLoading(true)
			await fetchPublicData()
			setLoading(false)
		}

		fetchData()
	}, [sortBy, sortOrder])

	const handleSort = (field: 'Name' | 'CLevel' | 'ResetCount') => {
		if (sortBy === field) {
			setSortOrder(sortOrder === 'DESC' ? 'ASC' : 'DESC')
		} else {
			setSortBy(field)
			setSortOrder('DESC')
		}
	}

	return (
		<ServerContext.Provider
			value={{
				topPlayers,
				serverStats,
				sortBy,
				sortOrder,
				loading,
				setSortBy,
				setSortOrder,
				handleSort,
			}}
		>
			{children}
		</ServerContext.Provider>
	)
}
